'use client';

import React, { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef?.current?.querySelectorAll('.scroll-reveal-init');
    elements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  const checkItems = [
  'Persönliche Betreuung durch Sulleyman Aktas',
  'Termingerechte Abgabe aller Erklärungen',
  'Digitale Buchhaltung mit Lexoffice',
  'Transparente Kommunikation ohne Fachchinesisch',
  'Langjährige Erfahrung mit KMUs und Selbstständigen'];


  return (
    <section id="ueber-uns" className="py-16 md:py-32 px-5 md:px-10 overflow-hidden" style={{ backgroundColor: 'var(--background)' }} ref={sectionRef}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <div className="lg:col-span-5 scroll-reveal-init">
            <div className="relative pb-8 lg:pb-0">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-black/10 border relative" style={{ borderColor: 'var(--border)' }}>
                <div className="aspect-[3/4] relative">
                  <img
                    alt="Modernes Bürogebäude mit Natursteinfassade und anthrazitfarbenen Fensterrahmen — Bürostandort Buchhaltungsbüro Aktas"
                    src="/assets/images/WhatsApp_Image_2026-08-04_at_18.50.31-1786097585284.jpeg"
                    className="object-cover bg-gray-200"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              </div>

              {/* Consultation room inset photo */}
              <div className="absolute bottom-0 -right-4 sm:-right-8 w-44 sm:w-56 h-36 sm:h-44 rounded-2xl overflow-hidden shadow-xl border-2" style={{ borderColor: 'var(--background)' }}>
                <img
                  src="/assets/images/Aktas_1_evoto_edited-1786030594633.jpeg"
                  alt="Beratungsraum mit Tisch und zwei Stühlen — persönliche Mandantenbetreuung bei Buchhaltungsbüro Aktas"
                  className="w-full h-full object-cover"
                  style={{ transform: 'translateZ(0)', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
                />
              </div>
              {/* Decorative circles */}
              <div
                className="absolute -top-6 -left-6 w-24 h-24 rounded-full border-2 -z-10"
                style={{
                  borderColor: 'rgba(200,150,90,0.2)',
                  background: 'radial-gradient(circle, rgba(200,150,90,0.08) 0%, transparent 70%)'
                }} />
              
              <div
                className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full border -z-10"
                style={{ borderColor: 'var(--border)', backgroundColor: 'rgba(232,228,223,0.5)' }} />
              
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-7 flex flex-col justify-between gap-7 lg:gap-8">
            {/* Header */}
            <div className="scroll-reveal-init stagger-2">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] mb-6" style={{ color: 'var(--accent)' }}>
                <span className="gold-dot" />
                Über uns
              </span>
              <h2 className="font-display text-hero-sm font-semibold mb-6 text-balance" style={{ color: 'var(--foreground)' }}>
                Buchhaltung mit{' '}
                <span className="italic">Persönlichkeit</span>
              </h2>
              <p className="text-base md:text-lg leading-relaxed font-light mb-4" style={{ color: 'var(--muted-foreground)' }}>
                Mein Name ist{' '}
                <strong className="font-semibold" style={{ color: 'var(--foreground)' }}>Sulleyman Aktas</strong>. Als Inhaber von Buchhaltungsbüro Aktas biete ich Ihnen professionelle Buchhaltungsdienstleistungen mit dem persönlichen Engagement, das Ihr Unternehmen verdient.
              </p>
              <p className="text-base leading-relaxed font-light" style={{ color: 'var(--muted-foreground)' }}>Mit langjähriger Erfahrung unterstütze ich Selbstständige, Freiberufler und kleine bis mittelständische Unternehmen dabei, ihre Finanzen in Ordnung zu halten — effizient, transparent und immer auf dem neuesten Stand der Gesetzgebung.

              </p>
            </div>

            {/* Checklist */}
            <div className="scroll-reveal-init stagger-3">
              <ul className="space-y-3">
                {checkItems?.map((item) =>
                <li key={item} className="flex items-start gap-3">
                    <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: 'rgba(200,150,90,0.15)' }}>
                    
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} style={{ color: 'var(--accent)' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{item}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Quote */}
            <div className="scroll-reveal-init stagger-4 border-l-2 pl-6 py-2" style={{ borderColor: 'var(--accent)' }}>
              <p className="text-base font-display italic leading-relaxed" style={{ color: 'rgba(28,28,30,0.8)' }}>
                „Ich betreue jeden Mandanten so, als wäre es mein eigenes Unternehmen — mit Sorgfalt, Verlässlichkeit und echtem Interesse an Ihrem Erfolg."
              </p>
              <p className="mt-3 text-sm font-semibold" style={{ color: 'var(--accent)' }}>— Sulleyman Aktas</p>
            </div>

            {/* CTA */}
            <div className="scroll-reveal-init stagger-5">
              <a
                href="#kontakt"
                onClick={(e) => {e?.preventDefault();document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });}}
                className="inline-flex items-center justify-center sm:justify-start gap-2 w-full sm:w-auto px-7 py-4 font-semibold text-sm rounded-full transition-all duration-300 hover:scale-[1.02] group min-h-[52px]"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary)'}>
                
                Persönliches Gespräch vereinbaren
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}